import {
  Tag,
  ListChecks,
  Hash,
  Text,
  User,
  Calendar,
  CheckSquare,
  Star,
  Calculator,
  Link2,
  BarChart3,
  GitPullRequest,
  Mail,
  Link,
  MousePointerClick,
  Phone,
  Paperclip,
  HardDrive,
  Clock,
  CircleUserRound,
  type LucideIcon,
} from "lucide-react";

export interface PropertyTypeMeta {
  type: string;
  label: string;
  icon: LucideIcon;
}

/**
 * Single source of truth for board property types → display label + default
 * icon. Import `PROPERTY_TYPES` (list) or `getPropertyIcon(type)` (icon lookup)
 * from here instead of re-declaring the mapping in each component.
 */
export const PROPERTY_TYPES: PropertyTypeMeta[] = [
  { type: "text", label: "Text", icon: Text },
  { type: "select", label: "Select", icon: Tag },
  { type: "multi_select", label: "Multi-Select", icon: ListChecks },
  { type: "status", label: "Status", icon: Tag },
  { type: "person", label: "Person", icon: User },
  { type: "date", label: "Date", icon: Calendar },
  { type: "checkbox", label: "Checkbox", icon: CheckSquare },
  { type: "priority", label: "Priority", icon: Star },
  { type: "id", label: "ID", icon: Hash },
  { type: "number", label: "Number", icon: Hash },
  { type: "relation", label: "Relation", icon: Link2 },
  { type: "rollup", label: "Rollup", icon: BarChart3 },
  { type: "formula", label: "Formula", icon: Calculator },
  { type: "created_time", label: "Created time", icon: Clock },
  { type: "created_by", label: "Created by", icon: CircleUserRound },
  { type: "last_edited_time", label: "Last edited time", icon: Clock },
  { type: "last_edited_by", label: "Last edited by", icon: CircleUserRound },
  { type: "github_pr", label: "GitHub PR", icon: GitPullRequest },
  { type: "google_drive_file", label: "Google Drive File", icon: HardDrive },
  { type: "email", label: "Email", icon: Mail },
  { type: "url", label: "URL", icon: Link },
  { type: "phone", label: "Phone", icon: Phone },
  { type: "file", label: "File / Media", icon: Paperclip },
  { type: "button", label: "Button", icon: MousePointerClick },
];

/**
 * Types a property can be CHANGED to. Type change is delete-and-recreate, so
 * any type the add-property flow can create qualifies. Callers must
 * special-case "relation": its creation needs a linked database, so the
 * picker flow has to run before the change is invoked.
 */
export const TYPE_CHANGE_TARGET_TYPES = [
  "text",
  "select",
  "multi_select",
  "status",
  "priority",
  "person",
  "date",
  "checkbox",
  "number",
  "email",
  "url",
  "phone",
  "file",
  "formula",
  "rollup",
  "relation",
  "github_pr",
  "button",
  "created_time",
  "created_by",
  "last_edited_time",
  "last_edited_by",
] as const;

// Shared feature gates for filter / sort / group .
// System columns (created/edited time and by) are not groupable yet: dropping a
// card into a group would write to a read-only column.
export const FILTERABLE_TYPES = ["text", "select", "multi_select", "status", "person", "date", "priority", "number", "relation", "rollup", "email", "url", "phone", "checkbox", "boolean", "formula", "id", "created_time", "created_by", "last_edited_time", "last_edited_by"];
export const SORTABLE_TYPES = ["text", "select", "multi_select", "status", "person", "date", "priority", "number", "relation", "rollup", "email", "url", "phone", "checkbox", "boolean", "formula", "id", "created_time", "created_by", "last_edited_time", "last_edited_by"];
export const GROUPABLE_TYPES = ["text", "select", "multi_select", "status", "person", "date", "priority", "number", "relation", "email", "url", "phone", "checkbox", "boolean"];
export const DUPLICATE_EXCLUDED_TYPES = ["id", "title", "button", "github_pr", "google_drive_file", "relation"];

export const isFilterableType = (type: string) => FILTERABLE_TYPES.includes(type);
export const isSortableType = (type: string) => SORTABLE_TYPES.includes(type);
export const isGroupableType = (type: string) => GROUPABLE_TYPES.includes(type);
export const isDuplicableType = (type: string) => !DUPLICATE_EXCLUDED_TYPES.includes(type);

/** Default icon component for a property type, or `null` if the type is unknown. */
export function getPropertyIcon(type: string): LucideIcon | null {
  return PROPERTY_TYPES.find((p) => p.type === type)?.icon ?? null;
}

/** Display label for a property type (falls back to the raw `type` string). */
export function getPropertyLabel(type: string): string {
  return PROPERTY_TYPES.find((p) => p.type === type)?.label ?? type;
}
